import { useEffect, useState } from "react";

import { ApolloError } from "@apollo/client";
import { Callout } from "@blueprintjs/core";

import { couldManageAttendees } from "@/components/helpers/meeting/is-pending-booking-request";
import { SwitchToWorkspace } from "@/components/helpers/workspace/SwitchToWorkspace";
import { Box } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import {
  InvitationStatus,
  JobStatus,
  JobType,
  useMeetingAttendeesLazyQuery,
  useWorkspaceScheduleJobsQuery,
} from "@/graphql";
import { Attendee, FullMeeting, UpcomingMeeting } from "@/types/meetings";
import { ensureNonNullArray } from "@/utils/array";

import { AttendeeList } from "./components/AttendeeList";
import { AttendeeManageSection } from "./components/AttendeeManageSection";

type Meeting = FullMeeting | UpcomingMeeting;

export function MeetingAttendees({ meeting }: { meeting: Meeting }) {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<ApolloError | null>(null);
  const { currentWorkspace } = useAuth();

  const {
    data: workspaceScheduleJobsData,
    refetch: refetchWorkspaceScheduleJobs,
    previousData: workspaceScheduleJobsPreviousData,
  } = useWorkspaceScheduleJobsQuery({
    variables: {
      jobTypes: [JobType.AddAttendeesToGroupMeeting],
      statuses: [JobStatus.Scheduled],
      meetingId: meeting ? parseInt(meeting?.id, 10) : undefined,
    },
    pollInterval: 60000,
  });

  const workspaceScheduleJobs = workspaceScheduleJobsData?.scheduledJobs || [];
  const scheduleJob = workspaceScheduleJobs?.[0];

  const [fetchAttendees, { loading: loadingAttendees }] =
    useMeetingAttendeesLazyQuery({
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const { attendees: attendeeList = [], total } =
          data?.meetingAttendees || {};
        setAttendees(ensureNonNullArray(attendeeList));
        setTotal(total || 0);
      },
      onError: (error) => {
        setError(error);
      },
    });

  const handleFetchAttendees = (
    meetingId: string,
    start: number,
    limit: number,
  ) => {
    setError(null);
    return fetchAttendees({
      variables: {
        meetingId,
        start,
        limit,
      },
    });
  };

  useEffect(() => {
    if (meeting?.id) {
      handleFetchAttendees(meeting.id, 0, 4);
    }
  }, [meeting]);

  useEffect(() => {
    if (workspaceScheduleJobsPreviousData !== workspaceScheduleJobsData) {
      refetchWorkspaceScheduleJobs();
    }
  }, [workspaceScheduleJobsPreviousData, workspaceScheduleJobsData]);

  let errorMsg: React.ReactNode | null = null;

  if (error) {
    const urlSlug = meeting.workspace?.urlSlug || "";
    const isNotInWorkspace = urlSlug !== currentWorkspace.urlSlug;
    errorMsg =
      isNotInWorkspace && meeting.workspace ? (
        <SwitchToWorkspace
          workspace={meeting.workspace}
          path={`?path=/sessions/${meeting.id}`}
        />
      ) : (
        <Callout intent={"danger"} icon={null}>
          <Box>{error.message}</Box>
        </Callout>
      );
  }
  const limit = 4;

  const remainNum = limit > 0 ? total - limit : 0;
  const existFailed = attendees.some(
    (attendee) => attendee?.status === InvitationStatus.Failed,
  );

  return (
    <Box pt={24} mb={24} className="bts">
      <Box fontWeight={500}>Attendees</Box>
      {existFailed && (
        <Callout intent="warning" icon={null}>
          Unable to send invites to some attendees!
        </Callout>
      )}
      <Box mt={2}>
        <AttendeeList
          loading={loadingAttendees}
          organizer={meeting.customer}
          attendees={attendees}
          total={total}
          mode="view-with-status"
          error={errorMsg}
          limit={limit}
        />
      </Box>
      {error || (remainNum <= 0 && !couldManageAttendees(meeting)) ? null : (
        <AttendeeManageSection
          meeting={meeting}
          attendees={attendees}
          onUpdateAttendee={() => {
            handleFetchAttendees(meeting.id, 0, 1000);
            refetchWorkspaceScheduleJobs();
          }}
          scheduleJob={scheduleJob}
        />
      )}
    </Box>
  );
}
